import type { ReactNode } from 'react'
import type { TipGoal } from '../../types/tipGoals'

/**
 * One tip goal: title, a progress bar, raised of target, and its state
 * (open with an optional deadline, reached, ended). Used by the creator's
 * Tip goals tab and by their public profile.
 */

export const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

export const goalState = (goal: TipGoal, now = new Date()): string => {
  if (goal.completed_at) return `Reached ${day(goal.completed_at)}`
  if (!goal.is_active) return 'Ended'
  if (goal.deadline && new Date(goal.deadline) <= now) return `Ended ${day(goal.deadline)}`
  return goal.deadline ? `Open until ${day(goal.deadline)}` : 'Open'
}

const TipGoalProgress = ({ goal, action }: { goal: TipGoal; action?: ReactNode }) => {
  const pct = Math.min(100, Math.max(0, goal.progress_pct))
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`tip-goal-${goal.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 break-words">{goal.title}</p>
          {goal.description && <p className="mt-0.5 text-sm text-gray-600 break-words">{goal.description}</p>}
        </div>
        {action}
      </div>
      <div
        className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-label={`${goal.title} progress`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div className="h-full rounded-full bg-primary-600" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 text-sm">
        <span className="tabular-nums text-gray-800">
          {dollars(goal.current_cents)} of {dollars(goal.target_cents)}
        </span>
        <span className="text-gray-500">{goalState(goal)}</span>
      </div>
    </div>
  )
}

export default TipGoalProgress
